import { convex } from '@/utils/convex';
import {
  getPolygonArea,
  isDuplicatePoint,
  isSelfIntersecting,
  wouldSelfIntersect,
  hasDuplicatePoints,
  removeCollinearPoints,
} from '../geoUtils';

export interface DrawingValidationResult {
  valid: boolean;
  reason?: string;
}

/**
 * 绘制校验器
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 实时校验：新点加入前是否会导致自相交或重复点
 * 2. 闭合校验：点数、重复点、自相交、凸多边形、最小面积
 * 3. 提供共线中间点清理，避免几乎共线顶点误判为凹
 *
 * 不操作地图，只做纯几何判断
 */
export class DrawingValidator {
  /** 最小允许的多边形面积（平方米），小于该值不允许完成绘制 */
  static readonly MIN_POLYGON_AREA_SQM = 0.2;

  /**
   * 校验即将加入的新点是否合法
   */
  validateNewPoint(
    newPoint: [number, number],
    existingPoints: [number, number][]
  ): DrawingValidationResult {
    const lastPoint = existingPoints[existingPoints.length - 1];
    if (lastPoint && isDuplicatePoint(newPoint, lastPoint)) {
      return { valid: false, reason: 'duplicate_point' };
    }

    if (wouldSelfIntersect(newPoint, existingPoints)) {
      return { valid: false, reason: 'would_self_intersect' };
    }

    return { valid: true };
  }

  /**
   * 校验闭合多边形是否合法
   */
  validatePolygon(
    points: [number, number][]
  ): DrawingValidationResult {
    if (points.length < 3) {
      return { valid: false, reason: 'not_enough_points' };
    }

    if (hasDuplicatePoints(points)) {
      return { valid: false, reason: 'duplicate_points' };
    }

    if (isSelfIntersecting(points)) {
      return { valid: false, reason: 'self_intersecting' };
    }

    const cleaned = removeCollinearPoints(points);
    if (cleaned.length < 3) {
      return { valid: false, reason: 'collinear_points' };
    }

    const coordinates = [...cleaned, cleaned[0]];
    if (convex(coordinates, coordinates.length - 1) !== 1) {
      return { valid: false, reason: 'concave_polygon' };
    }

    const area = getPolygonArea(cleaned);
    if (area < DrawingValidator.MIN_POLYGON_AREA_SQM) {
      return { valid: false, reason: 'area_too_small' };
    }

    return { valid: true };
  }

  /**
   * 将原因码转换为中文提示（供业务层展示 toast）
   */
  static getReasonText(reason: string): string {
    const map: Record<string, string> = {
      duplicate_point: '该点与上一点重复',
      would_self_intersect: '该点会导致多边形自相交，请选择其他位置',
      not_enough_points: '至少需要绘制 3 个点',
      duplicate_points: '存在重复顶点，请删除后重试',
      self_intersecting: '多边形自相交，请调整顶点位置',
      collinear_points: '有效顶点不足 3 个（存在共线顶点）',
      concave_polygon: '当前仅支持凸多边形，检测到凹边形',
      area_too_small: `多边形面积过小，请至少绘制 ${DrawingValidator.MIN_POLYGON_AREA_SQM} 平方米以上的区域`,
    };
    return map[reason] ?? '绘制校验失败';
  }
}
